import { Button, FiltersIcon, Modal } from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import { useEffect, useState } from 'react';
import { useNavigation } from 'react-router';

import { ClientOnly } from '@jane/bloom/components/clientOnly';
import { FilterActions } from '@jane/bloom/components/productListingPage/categoryAndFilterSelectors/filterSelector/filterActions';
import {
  useBloomFilters,
  useProductSearchFilters,
  useSearchParamFilters,
} from '@jane/bloom/hooks/filters';
import type { SortOptions } from '@jane/bloom/types/schemas/algolia/product';
import { isEmpty } from '@jane/bloom/utils/guards/isEmpty';
import { getFacetRange } from '@jane/bloom/viewModels/facetRange';
import type { ProductSearchFiltersViewModel } from '@jane/bloom/viewModels/productSearchFilters';

import { ModalButtonFilterSection } from './allFiltersModal/buttonFilterSection';
import { DealsAndRewardsSection } from './allFiltersModal/dealsAndRewardsSection';
import {
  ModalFilterEntrypoint,
  ModalFilterSection,
} from './allFiltersModal/modalFiltersSection';
import { PotencySection } from './allFiltersModal/potencySection';
import { RatingSection } from './allFiltersModal/ratingSection';
import { SortSection } from './allFiltersModal/sortSection';

import styles from './allFiltersModal.module.css';

interface FiltersButtonProps {
  availableProductsCount?: number;
  currentSort: SortOptions;
  sortOptions: LabeledOption<SortOptions>[];
}

export function AllFiltersModal({
  availableProductsCount,
  currentSort,
  sortOptions,
}: FiltersButtonProps) {
  const {
    getPotencyFilterKeysNonEmpty,
    getProductSearchFilterOptions,
    janeGoldFilterOption,
    productSearchFilters,
  } = useProductSearchFilters();
  const { shouldShowGold, sortedAllDisplayFilterKey } = useBloomFilters();
  const { clearAllFilters, searchParams, setSearchParams } =
    useSearchParamFilters();
  const [resetting, setResetting] = useState(false);
  const { state } = useNavigation();

  const [originalFilters, setOriginalFilters] = useState({});

  const [open, setOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<string>('default');

  const validPotencySliderKeys = getPotencyFilterKeysNonEmpty().filter(
    (key) => {
      const filterList = getProductSearchFilterOptions(key);
      const { rangeMax, rangeMin } = getFacetRange(filterList);
      return rangeMax > rangeMin;
    }
  );

  const [saleItem] = getProductSearchFilterOptions('sale');
  const goldItem = shouldShowGold ? janeGoldFilterOption : undefined;

  const sections = sortedAllDisplayFilterKey
    .filter(
      (filterKey) =>
        [
          'activities',
          'ratings',
          'potencies',
          'prices',
          'weights',
          'specials',
          'terpenes',
        ].includes(filterKey) ||
        productSearchFilters?.[filterKey as keyof ProductSearchFiltersViewModel]
          ?.length ||
        -1 > 0
    )
    .map((filterKey) => {
      switch (filterKey) {
        case 'ratings': {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <RatingSection key={filterKey} />
            )
          );
        }
        case 'potencies': {
          return (
            validPotencySliderKeys.length > 0 && (
              <PotencySection key={filterKey} resetting={resetting} />
            )
          );
        }
        case 'prices': {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <ModalButtonFilterSection key={filterKey} filterKey={filterKey} />
            )
          );
        }
        case 'brands': {
          return (
            !isEmpty(productSearchFilters?.['brands']) && (
              <ModalFilterEntrypoint
                key={filterKey}
                filterKey={filterKey}
                handleClick={() => setSelectedTab('brands')}
              />
            )
          );
        }
        case 'terpenes': {
          return (
            !isEmpty(productSearchFilters?.['terpenes']) && (
              <ModalFilterEntrypoint
                key={filterKey}
                filterKey={filterKey}
                handleClick={() => setSelectedTab('terpenes')}
              />
            )
          );
        }
        case 'jane-gold': {
          return false;
        }
        default: {
          return (
            getProductSearchFilterOptions(filterKey).length > 0 && (
              <ModalButtonFilterSection
                key={filterKey}
                filterKey={filterKey}
                icon
                shouldShowDividerBelow={
                  sortedAllDisplayFilterKey[
                    sortedAllDisplayFilterKey.length - 1
                  ] !== filterKey
                }
              />
            )
          );
        }
      }
    });

  useEffect(() => {
    if (resetting && state === 'idle') {
      setResetting(false);
    }
  }, [resetting, state]);

  const renderView = () => {
    switch (selectedTab) {
      case 'brands':
        return (
          <ModalFilterSection selectedTab={selectedTab} resetting={resetting} />
        );
      case 'terpenes':
        return (
          <ModalFilterSection selectedTab={selectedTab} resetting={resetting} />
        );
      default:
        return (
          <>
            <Modal.Header
              className={styles['modalHeader']}
              title="Filters and sort"
              textAlign="center"
            />
            <Modal.Content>
              <SortSection
                sortOptions={sortOptions}
                currentSort={currentSort}
              />
              {(goldItem || saleItem) && (
                <DealsAndRewardsSection
                  goldItem={goldItem}
                  saleItem={saleItem}
                />
              )}
              {sections}
            </Modal.Content>
          </>
        );
    }
  };

  return (
    <>
      <Button.Icon
        variant="tertiary"
        icon={<FiltersIcon />}
        label="All filters"
        key="allFilters"
        ariaLabel="All Filters"
        onClick={() => {
          setOriginalFilters(searchParams);
          setOpen(true);
        }}
      />
      <ClientOnly>
        {(isMobile) => (
          <Modal
            background="grays-white"
            open={open}
            onRequestClose={
              selectedTab === 'default'
                ? () => {
                    setOpen(false);
                    setSearchParams(originalFilters);
                  }
                : () => setSelectedTab('default')
            }
            id="parent"
            variant={isMobile ? 'full-screen' : 'standard'}
            overlayClose
          >
            {renderView()}
            <Modal.Footer>
              <FilterActions
                availableProductsCount={availableProductsCount}
                fullButtons={{ clear: true, show: true }}
                handleClearAll={clearAllFilters}
                handleClose={() => setOpen(false)}
              />
            </Modal.Footer>
          </Modal>
        )}
      </ClientOnly>
    </>
  );
}
